import { zonedDate } from '../../utils/date-time';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Agendamento, StatusHorario } from '../agenda/Enum';
import { formatBRL, getScheduleStatusLabel, getScheduleTone, getScheduleTotal } from '../agenda/cliente/List/helpers';
import { UserService } from '../configuracoes/usuario/Service';
import { Modal } from '../../components/Modal';
import ScheduleView from '../agenda/estabelecimento/view/ScheduleView';
import { formatMes } from '../recorrencias/helpers';
import './Clientes.css';
import { Calendar, TriangleAlert } from 'lucide-react';

const userService = new UserService();

export const AgendamentosClient = ({ clientId }: { clientId: string }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [agendamentos, setAgendamentos] = useState<Agendamento[]>([]);
    const [selectedSchedule, setSelectedSchedule] = useState<Agendamento | undefined>(undefined);

    const load = useCallback(async () => {
        try {
            setError(false);
            const { data } = await userService.getAgendamentoByClientId(clientId);
            setAgendamentos(data);
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [clientId]);

    useEffect(() => {
        setLoading(true);
        load();
    }, [load]);

    // A API ja devolve do mais recente para o mais antigo.
    const meses = useMemo(() => {
        const grupos: { key: string; label: string; items: Agendamento[] }[] = [];

        agendamentos.forEach((agendamento) => {
            const key = zonedDate(agendamento.startAt, agendamento.timezone).format('YYYY-MM');
            const atual = grupos[grupos.length - 1];

            if (atual?.key === key) {
                atual.items.push(agendamento);
            } else {
                grupos.push({ key, label: formatMes(agendamento.startAt, agendamento.timezone), items: [agendamento] });
            }
        });

        return grupos;
    }, [agendamentos]);

    if (loading) {
        return (
            <div className="space-y-2" aria-busy="true">
                {[0, 1, 2].map((item) => (
                    <div key={item} className="sf-skeleton h-16" />
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>Não foi possível carregar os agendamentos</h2>
                <button type="button" className="btn btn-primary btn-sm" onClick={load}>
                    Tentar novamente
                </button>
            </div>
        );
    }

    if (!agendamentos.length) {
        return (
            <div className="sf-state">
                <Calendar aria-hidden />
                <h2>Nenhum agendamento</h2>
                <p>Os agendamentos deste cliente no estabelecimento aparecem aqui.</p>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {!!selectedSchedule && (
                <Modal
                    onClose={() => {
                        setSelectedSchedule(undefined);
                        load();
                    }}
                    title="Agendamento"
                >
                    <ScheduleView schedule={selectedSchedule} />
                </Modal>
            )}

            {meses.map((mes) => (
                <section key={mes.key}>
                    <h3 className="sf-group-title">{mes.label}</h3>
                    <ul className="sf-group">
                        {mes.items.map((agendamento) => {
                            const start = zonedDate(agendamento.startAt, agendamento.timezone);
                            const isCancelled = agendamento.status === StatusHorario.CANCELADO;

                            return (
                                <li key={agendamento._id}>
                                    <button
                                        type="button"
                                        className={`cli-appt ${isCancelled ? 'is-cancelled' : ''}`}
                                        onClick={() => setSelectedSchedule(agendamento)}
                                    >
                                        <span className="sf-date" aria-hidden>
                                            <span>{start.format('ddd').replace('.', '')}</span>
                                            <strong>{start.format('DD')}</strong>
                                        </span>
                                        <span className="cli-row-body">
                                            <span className="cli-row-title">
                                                <span className="sr-only">{start.format('dddd, DD/MM')} </span>
                                                {start.format('HH:mm')} · {agendamento.service?.name || 'Serviço não informado'}
                                                {agendamento.servicosExtras?.length
                                                    ? ` +${agendamento.servicosExtras.length}`
                                                    : ''}
                                            </span>
                                            <span className="cli-row-sub">
                                                {agendamento.attendant?.name || 'Atendente não informado'}
                                            </span>
                                            <span className="sf-tags">
                                                <span className="sf-tag" data-tone={getScheduleTone(agendamento.status)}>
                                                    {getScheduleStatusLabel(agendamento)}
                                                </span>
                                            </span>
                                        </span>
                                        <span className="cli-appt-price">{formatBRL(getScheduleTotal(agendamento))}</span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            ))}
        </div>
    );
};
